import {
  Briefcase,
  Building2,
  Cake,
  Calendar,
  Camera,
  Coffee,
  Film,
  Flower2,
  Gift,
  GraduationCap,
  HandHeart,
  Heart,
  Mic,
  Music,
  PartyPopper,
  Plane,
  Presentation,
  ShoppingBag,
  Sparkles,
  Star,
  Ticket,
  Trophy,
  UtensilsCrossed,
  Users,
  Wine,
  type LucideIcon,
} from 'lucide-react'
import type { BadgeVariant } from './enum-utils'

/**
 * Curated allowlist of icons an organization may pick for an event type.
 *
 * Icons are resolved through this static map rather than a dynamic Lucide
 * lookup so the bundler can tree-shake everything else out.
 *
 * The first seven entries are the tokens seeded by migration
 * `000279_add_organization_event_types.sql`; the rest give custom types a
 * reasonable spread to choose from.
 */
export const EVENT_TYPE_ICONS = {
  briefcase: Briefcase,
  presentation: Presentation,
  heart: Heart,
  'heart-handshake': HandHeart,
  'party-popper': PartyPopper,
  'shopping-bag': ShoppingBag,
  calendar: Calendar,
  star: Star,
  music: Music,
  cake: Cake,
  utensils: UtensilsCrossed,
  camera: Camera,
  users: Users,
  building: Building2,
  gift: Gift,
  sparkles: Sparkles,
  trophy: Trophy,
  coffee: Coffee,
  'graduation-cap': GraduationCap,
  mic: Mic,
  plane: Plane,
  flower: Flower2,
  film: Film,
  wine: Wine,
  ticket: Ticket,
} as const satisfies Record<string, LucideIcon>

export type EventTypeIconToken = keyof typeof EVENT_TYPE_ICONS

/** Icon used when a type has no icon, or an icon this build doesn't know. */
export const DEFAULT_EVENT_TYPE_ICON: EventTypeIconToken = 'calendar'

export const EVENT_TYPE_ICON_TOKENS = Object.keys(
  EVENT_TYPE_ICONS
) as EventTypeIconToken[]

/**
 * Resolve a stored icon token to a Lucide component, falling back to the
 * default when the token isn't in the allowlist.
 */
export function resolveEventTypeIcon(icon: string | undefined): LucideIcon {
  if (icon && icon in EVENT_TYPE_ICONS) {
    return EVENT_TYPE_ICONS[icon as EventTypeIconToken]
  }
  return EVENT_TYPE_ICONS[DEFAULT_EVENT_TYPE_ICON]
}

/**
 * Color tokens an organization may pick, mapped onto the app's
 * {@link BadgeVariant} palette.
 *
 * The keys are the tokens stored in `organizations.event_types.color` — the
 * seed migration writes plain color names (`blue`, `amber`, `gray`, ...), so
 * those are the canonical spellings. The `BadgeVariant` names are also
 * accepted on read so hand-written rows still render.
 */
export const EVENT_TYPE_COLORS = {
  blue: 'info',
  purple: 'purple',
  pink: 'pink',
  green: 'success',
  orange: 'orange',
  amber: 'warning',
  red: 'danger',
  gray: 'secondary',
} as const satisfies Record<string, BadgeVariant>

export type EventTypeColorToken = keyof typeof EVENT_TYPE_COLORS

/** Color used when a type has no color, or a color this build doesn't know. */
export const DEFAULT_EVENT_TYPE_COLOR: EventTypeColorToken = 'gray'

export const EVENT_TYPE_COLOR_TOKENS = Object.keys(
  EVENT_TYPE_COLORS
) as EventTypeColorToken[]

const BADGE_VARIANT_PASSTHROUGH: ReadonlySet<string> = new Set<BadgeVariant>([
  'default',
  'secondary',
  'success',
  'warning',
  'danger',
  'info',
  'purple',
  'pink',
  'orange',
])

/**
 * Resolve a stored color token to a badge variant. Accepts both the picker
 * tokens (`blue`) and raw badge variants (`info`), falling back to `default`.
 */
export function resolveEventTypeColor(color: string | undefined): BadgeVariant {
  if (!color) return 'default'
  if (color in EVENT_TYPE_COLORS) {
    return EVENT_TYPE_COLORS[color as EventTypeColorToken]
  }
  if (BADGE_VARIANT_PASSTHROUGH.has(color)) {
    return color as BadgeVariant
  }
  return 'default'
}
